import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  type GeneralFilterGroup,
  GeneralFiltersContextProvider,
} from '../../general-filters';
import { type SelectedGeneralFilters } from '../../general-filters/types';
import { areGeneralFiltersEqual } from '../utilities/FiltersDrawer.utils';
import { compactSelectedGeneralFilters } from '../utilities/FiltersDrawer.utils';

type GeneralFiltersStateContextValue = {
  hasGeneralFilters: boolean;
  isGeneralFiltersDirty: boolean;
  syncFromExternalGeneralState: () => void;
  getPreparedGeneralFilters: () => SelectedGeneralFilters;
  hydrateGeneralFilters: (filters: SelectedGeneralFilters) => void;
  selectedGeneralFilters: SelectedGeneralFilters;
};

const GeneralFiltersStateContext =
  createContext<GeneralFiltersStateContextValue | null>(null);

interface GeneralFiltersStateProviderProps extends PropsWithChildren {
  filterGroups: GeneralFilterGroup[];
  selectedGeneralFilters: SelectedGeneralFilters;
}

export const GeneralFiltersStateProvider = ({
  filterGroups,
  selectedGeneralFilters,
  children,
}: GeneralFiltersStateProviderProps) => {
  const [localSelectedGeneralFilters, setLocalSelectedGeneralFilters] =
    useState<SelectedGeneralFilters>(selectedGeneralFilters);

  const syncFromExternalGeneralState = useCallback(() => {
    setLocalSelectedGeneralFilters(selectedGeneralFilters);
  }, [selectedGeneralFilters]);

  const hasGeneralFilters = useMemo(
    () =>
      Object.keys(localSelectedGeneralFilters).some((key) =>
        Object.values(localSelectedGeneralFilters[key] || {}).some(Boolean),
      ),
    [localSelectedGeneralFilters],
  );

  const isGeneralFiltersDirty = useMemo(
    () =>
      !areGeneralFiltersEqual(
        localSelectedGeneralFilters,
        selectedGeneralFilters,
      ),
    [localSelectedGeneralFilters, selectedGeneralFilters],
  );

  const getPreparedGeneralFilters = useCallback(() => {
    return compactSelectedGeneralFilters(localSelectedGeneralFilters);
  }, [localSelectedGeneralFilters]);

  const providerValue = useMemo(
    () => ({
      hasGeneralFilters,
      isGeneralFiltersDirty,
      syncFromExternalGeneralState,
      getPreparedGeneralFilters,
      hydrateGeneralFilters: setLocalSelectedGeneralFilters,
      selectedGeneralFilters,
    }),
    [
      hasGeneralFilters,
      isGeneralFiltersDirty,
      syncFromExternalGeneralState,
      getPreparedGeneralFilters,
      setLocalSelectedGeneralFilters,
      selectedGeneralFilters,
    ],
  );

  useEffect(() => {
    syncFromExternalGeneralState();
  }, [selectedGeneralFilters, syncFromExternalGeneralState]);

  return (
    <GeneralFiltersStateContext.Provider value={providerValue}>
      <GeneralFiltersContextProvider
        filterGroups={filterGroups}
        selectedFilters={localSelectedGeneralFilters}
        onSelectedFiltersChange={setLocalSelectedGeneralFilters}
      >
        {children}
      </GeneralFiltersContextProvider>
    </GeneralFiltersStateContext.Provider>
  );
};

export const useGeneralFiltersStateContext = () => {
  const context = useContext(GeneralFiltersStateContext);
  if (!context) {
    throw new Error(
      'useGeneralFiltersStateContext must be used within GeneralFiltersStateProvider',
    );
  }
  return context;
};
